<template>
  <div class="card">
    <div class="card-body">
      <div class="row gy-sm-1 align-items-center">
        <div class="col-md-3">
          <Select v-model="selectedStore" :options="availableStores" optionLabel="name" optionValue="id"
                  placeholder="Магазин не выбран" style="width: 100%" @change="onStoreChange">
            <template #value="slotProps">
              <div class="d-flex align-items-center" v-if="slotProps.value">
                <img :src="storeIcon(slotProps.value)" alt="" width="18" class="me-2" v-if="storeIcon(slotProps.value)">
                <span>{{ storeName(slotProps.value) }}</span>
              </div>
              <span v-else>{{ slotProps.placeholder }}</span>
            </template>
            <template #option="slotProps">
              <div class="d-flex align-items-center">
                <img :src="slotProps.option.icon" alt="" width="18" class="me-2" v-if="slotProps.option.icon">
                <span>{{ slotProps.option.name }}</span>
              </div>
            </template>
          </Select>
        </div>
        <div class="col-md-4">
          <div class="input-group input-group-merge shadow-none">
            <input type="text" class="form-control border-1 py-2" placeholder="Поиск по наименованию"
                   v-model="searchName" @keyup.enter="onSearch" @change="onSearch">
          </div>
        </div>
        <div class="col-md-3 d-flex align-items-center">
          <div class="form-check form-switch ps-0 mb-0 d-flex align-items-center">
            <input class="form-check-input m-0 me-2" type="checkbox" id="onlyTracked"
                   v-model="onlyTracked" @change="onOnlyTrackedChange">
            <label class="form-check-label text-nowrap mb-0" for="onlyTracked">Только отслеживаемые</label>
          </div>
        </div>
        <div class="col-md-2 d-flex align-items-center justify-content-end gap-2">
          <span class="text-muted text-nowrap">Строк на странице:</span>
          <Select v-model="pageSize" :options="pageSizes" @change="onPageSizeChange" />
        </div>
      </div>

      <div v-if="selectedOfferIds.length" class="d-flex align-items-center gap-2 mt-3">
        <span class="text-muted">Выбрано: {{ selectedOfferIds.length }}</span>
        <button type="button" class="btn btn-sm btn-primary" @click="bulkSetTracking(true)">
          Включить отслеживание
        </button>
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="bulkSetTracking(false)">
          Выключить
        </button>
      </div>

      <div v-if="!rows.length && !loadingProducts" class="mt-7">
        Товары не найдены
      </div>
    </div>

    <LoadingBlock :loading="loadingProducts">
      <div class="table-responsive" v-if="rows.length">
        <table class="table showcase-table align-middle">
          <thead>
          <tr>
            <th style="width: 40px">
              <input type="checkbox" class="form-check-input" :checked="allSelected" @change="toggleSelectAll">
            </th>
            <th>НАИМЕНОВАНИЕ ТОВАРА</th>
            <th style="width: 150px">ТЕКУЩАЯ ЦЕНА</th>
            <th style="width: 340px">ЦЕНЫ НА ВИТРИНЕ</th>
            <th style="width: 140px" class="text-end">ОТСЛЕЖИВАНИЕ</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="row in rows" :key="row.id">
            <td>
              <input type="checkbox" class="form-check-input" :disabled="!row.offer"
                     :checked="row.offer && selectedOfferIds.includes(row.offer.id)"
                     @change="toggleSelect(row)">
            </td>
            <td>
              <a v-if="row.offer && row.offer.store_url" :href="row.offer.store_url" target="_blank"
                 rel="noopener noreferrer" class="fw-medium product-name product-link"
                 title="Открыть на маркетплейсе">{{ row.name }}</a>
              <div v-else class="fw-medium product-name">{{ row.name }}</div>
              <div class="text-muted small" v-if="row.code">SKU {{ row.code }}</div>
            </td>
            <td>
              <span class="fw-semibold" v-if="row.offer">{{ formatMoney(row.offer.price) }}</span>
              <span class="text-muted" v-else-if="loadingOffers"></span>
              <span class="text-muted" v-else>—</span>
            </td>
            <td>
              <template v-if="!row.offer && loadingOffers">
                <div class="d-flex align-items-center text-muted">
                  <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                  Загрузка…
                </div>
              </template>

              <template v-else-if="!row.offer">
                <span class="text-muted">Нет оффера в магазине</span>
              </template>

              <template v-else-if="rowState(row) === 'disabled'">
                <div class="showcase-disabled">
                  <span class="disabled-icon"><i class="ti ti-eye-off"></i></span>
                  <div class="flex-grow-1">
                    <div class="fw-medium">Отслеживание отключено</div>
                    <div class="text-muted small">Включите, чтобы получать цены витрины</div>
                  </div>
                  <button type="button" class="btn btn-sm btn-label-primary" @click="setTracking(row, true)">
                    Включить
                  </button>
                </div>
              </template>

              <template v-else-if="rowState(row) === 'waiting'">
                <div class="showcase-waiting">
                  <span class="waiting-icon"><i class="ti ti-refresh"></i></span>
                  <div>
                    <div class="fw-medium">Данные обновляются</div>
                    <div class="small">Первые цены появятся через 5–10 минут</div>
                  </div>
                </div>
              </template>

              <template v-else-if="rowState(row) === 'has_discount'">
                <div class="showcase-prices">
                  <div class="showcase-price-line" v-for="(line, i) in discountLines(row)" :key="i">
                    <span class="price-dot" :class="line.type"></span>
                    <span class="price-label">{{ line.label }}</span>
                    <span class="price-badge">-{{ formatPercent(line.percent) }}%</span>
                    <span class="price-new" :class="line.type">{{ formatMoney(line.newPrice) }}</span>
                  </div>
                  <div class="text-muted small mt-1 showcase-updated" v-if="row.offer.discount_data && row.offer.discount_data.synced_at"
                       v-tooltip.bottom="formatSyncedAt(row.offer.discount_data.synced_at)">
                    <span class="ti ti-clock ti-xs me-1"></span>
                    обновлено {{ timeAgo(row.offer.discount_data.synced_at) }}
                  </div>
                </div>
              </template>

              <template v-else>
                <div class="text-muted">
                  <span>Товар не найден</span>
                  <i class="ti ti-info-circle ti-xs ms-1" style="cursor: help;"
                     v-tooltip.top="'Не удалось открыть страницу товара, либо товара нет в наличии'"></i>
                  <div class="small" v-if="row.offer.discount_data && row.offer.discount_data.synced_at"
                       v-tooltip.bottom="formatSyncedAt(row.offer.discount_data.synced_at)">
                    обновлено {{ timeAgo(row.offer.discount_data.synced_at) }}
                  </div>
                </div>
              </template>
            </td>
            <td class="text-end">
              <ToggleSwitch v-if="row.offer" :modelValue="!!row.offer.discount_sync_enabled"
                            @update:modelValue="val => setTracking(row, val)" />
              <span class="text-muted" v-else>—</span>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <BasePagination :info="paginationInfo" ref="pagination" @changePage="changePage" />
    </LoadingBlock>
  </div>
</template>

<script>
import {user} from "@/data/user";
import {stores} from "@/data/store";
import {getProductsPost} from "@/api/product";
import {getOffers, setDiscountSync} from "@/api/offer";
import {timeAgo, formatDateTime} from "@/helpers/date";
import {Message, Messages} from "@/data/messages";
import LoadingBlock from "@/components/page/LoadingBlock.vue";
import BasePagination from "@/components/list/Pagination.vue";
import {Select, ToggleSwitch} from "primevue";
import Tooltip from "primevue/tooltip";

const STORAGE_KEY = 'showcase_active_store'
const POLL_INTERVAL = 25000

export default {
  name: 'ShowcasePrices',
  components: {LoadingBlock, BasePagination, Select, ToggleSwitch},
  directives: {tooltip: Tooltip},
  data() {
    return {
      allStores: [],
      selectedStore: null,
      rows: [],
      searchName: '',
      onlyTracked: false,
      page: 1,
      pageSize: 25,
      pageSizes: [10, 25, 50, 100],
      paginationInfo: {},
      loadingProducts: false,
      loadingOffers: false,
      selectedOfferIds: [],
      pollTimer: null,
      orderType: 'fbs',
      companyTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }
  },
  computed: {
    availableStores() {
      return this.allStores.filter(s => s.marketplace === 'ozon' || s.marketplace === 'wb')
    },
    selectedMarketplace() {
      const store = this.allStores.find(s => s.id === this.selectedStore)
      return store ? store.marketplace : null
    },
    allSelected() {
      const selectable = this.rows.filter(r => r.offer)
      return selectable.length > 0 && selectable.every(r => this.selectedOfferIds.includes(r.offer.id))
    }
  },
  methods: {
    timeAgo,
    formatSyncedAt(date) {
      return formatDateTime(date, this.companyTimezone)
    },
    storeIcon(id) {
      const store = this.allStores.find(s => s.id === id)
      return store ? store.icon : ''
    },
    storeName(id) {
      const store = this.allStores.find(s => s.id === id)
      return store ? store.name : ''
    },
    formatMoney(value) {
      if (value === null || value === undefined || isNaN(value)) {
        return '—'
      }
      return `${Math.round(value).toLocaleString('ru-RU')} ₽`
    },
    formatPercent(value) {
      return Number(value.toFixed(1)).toString()
    },
    async loadStores() {
      this.allStores = await stores.getStores()
      const company = await user.getActiveCompany()
      const saved = this.readSavedStore(company)
      if (saved && this.availableStores.some(s => s.id === saved)) {
        this.selectedStore = saved
      } else if (this.availableStores.length) {
        this.selectedStore = this.availableStores[0].id
      }
    },
    readSavedStore(company) {
      try {
        const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
        return raw && typeof raw === 'object' ? raw[company] : null
      } catch (e) {
        return null
      }
    },
    saveSelectedStore(company) {
      let raw
      try {
        raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
      } catch (e) {
        raw = {}
      }
      if (!raw || raw.constructor !== Object) {
        raw = {}
      }
      raw[company] = this.selectedStore
      localStorage.setItem(STORAGE_KEY, JSON.stringify(raw))
    },
    async onStoreChange() {
      const company = await user.getActiveCompany()
      this.saveSelectedStore(company)
      this.page = 1
      this.selectedOfferIds = []
      await this.loadData()
    },
    onSearch() {
      this.page = 1
      this.loadData()
    },
    onOnlyTrackedChange() {
      this.page = 1
      this.loadData()
    },
    onPageSizeChange() {
      this.page = 1
      this.loadData()
    },
    changePage(page) {
      this.page = page
      this.loadData()
    },
    async loadData() {
      this.stopPolling()
      await this.loadProducts()
      await this.loadOffers()
      this.maybePoll()
    },
    async loadProducts() {
      this.loadingProducts = true
      const company = await user.getActiveCompany()
      const filters = {
        company,
        page: this.page,
        size: this.pageSize,
        offer_exists: true,
        offer_store_id: this.selectedStore,
        order_type: this.orderType
      }
      if (this.onlyTracked) {
        filters.offer_discount_sync_enabled = true
      }
      if (this.searchName) {
        filters.name = this.searchName
      }
      try {
        const result = await getProductsPost(filters)
        this.paginationInfo = {
          total: result.data.total,
          pages: result.data.pages,
          page: result.data.page,
          size: result.data.size
        }
        this.page = result.data.page
        this.rows = result.data.items.map(product => ({
          id: product.id,
          name: product.name,
          code: product.code,
          offer: null
        }))
      } catch (e) {
        console.error('Error loading products', e)
        this.rows = []
      } finally {
        this.loadingProducts = false
      }
    },
    async loadOffers() {
      if (!this.rows.length || !this.selectedStore) {
        return
      }
      this.loadingOffers = true
      try {
        const company = await user.getActiveCompany()
        const productIds = this.rows.map(r => r.id)
        const result = await getOffers(company, this.orderType, [this.selectedStore], productIds)
        const offersByProduct = {}
        result.data.items.forEach(offer => {
          offersByProduct[offer.product_id] = offer
        })
        this.rows.forEach(row => {
          const offer = offersByProduct[row.id]
          row.offer = offer
            ? {
              id: offer.id,
              price: offer.price,
              store_url: offer.store_url,
              discount_sync_enabled: offer.discount_sync_enabled,
              discount_data: offer.discount_data
            }
            : null
        })
        this.selectedOfferIds = this.selectedOfferIds.filter(
          id => this.rows.some(r => r.offer && r.offer.id === id)
        )
      } catch (e) {
        console.error('Error loading offers', e)
      } finally {
        this.loadingOffers = false
      }
    },
    marketplacePercents(row) {
      const dd = row.offer && row.offer.discount_data
      if (!dd) {
        return {defaultPercent: null, cardPercent: null, cardLabel: ''}
      }
      if (this.selectedMarketplace === 'wb') {
        return {
          defaultPercent: dd.wb_default_percent,
          cardPercent: dd.wb_card_percent,
          cardLabel: 'По карте WB'
        }
      }
      return {
        defaultPercent: dd.ozon_default_percent,
        cardPercent: dd.ozon_card_percent,
        cardLabel: 'По карте Ozon'
      }
    },
    rowState(row) {
      if (!row.offer) {
        return 'no_offer'
      }
      if (!row.offer.discount_sync_enabled) {
        return 'disabled'
      }
      const dd = row.offer.discount_data
      if (!dd || !dd.synced_at) {
        return 'waiting'
      }
      const {defaultPercent, cardPercent} = this.marketplacePercents(row)
      const hasDiscount = (defaultPercent > 0) || (cardPercent > 0)
      return hasDiscount ? 'has_discount' : 'no_discount'
    },
    discountLines(row) {
      const {defaultPercent, cardPercent, cardLabel} = this.marketplacePercents(row)
      const price = row.offer.price
      const lines = []
      if (defaultPercent > 0) {
        lines.push({
          type: 'default',
          label: 'Без карты',
          percent: defaultPercent,
          oldPrice: price,
          newPrice: price * (1 - defaultPercent / 100)
        })
      }
      if (cardPercent > 0) {
        lines.push({
          type: 'card',
          label: cardLabel,
          percent: cardPercent,
          oldPrice: price,
          newPrice: price * (1 - cardPercent / 100)
        })
      }
      return lines
    },
    async setTracking(row, enabled) {
      if (!row.offer) {
        return
      }
      const previous = row.offer.discount_sync_enabled
      row.offer.discount_sync_enabled = enabled
      if (!enabled) {
        row.offer.discount_data = null
      }
      try {
        const company = await user.getActiveCompany()
        await setDiscountSync(company, [row.offer.id], enabled)
        this.maybePoll()
      } catch (e) {
        row.offer.discount_sync_enabled = previous
        this.handleSyncError(e)
      }
    },
    async bulkSetTracking(enabled) {
      if (!this.selectedOfferIds.length) {
        return
      }
      const ids = [...this.selectedOfferIds]
      try {
        const company = await user.getActiveCompany()
        await setDiscountSync(company, ids, enabled)
        this.rows.forEach(row => {
          if (row.offer && ids.includes(row.offer.id)) {
            row.offer.discount_sync_enabled = enabled
            if (!enabled) {
              row.offer.discount_data = null
            }
          }
        })
        this.maybePoll()
      } catch (e) {
        this.handleSyncError(e)
        await this.loadOffers()
      }
    },
    handleSyncError(e) {
      const message = new Message()
      message.type = 'failure'
      message.format = 'notification'
      const status = e && e.response && e.response.status
      if (status === 403) {
        message.text = (e.response.data && e.response.data.detail)
          || 'Превышен лимит отслеживаемых офферов по подписке'
      } else if (status === 404) {
        message.text = 'Офферы не найдены. Список будет обновлён.'
      } else {
        message.text = 'Не удалось изменить отслеживание. Попробуйте снова.'
      }
      Messages.list.push(message)
    },
    toggleSelect(row) {
      if (!row.offer) {
        return
      }
      const id = row.offer.id
      if (this.selectedOfferIds.includes(id)) {
        this.selectedOfferIds = this.selectedOfferIds.filter(i => i !== id)
      } else {
        this.selectedOfferIds = [...this.selectedOfferIds, id]
      }
    },
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedOfferIds = []
      } else {
        this.selectedOfferIds = this.rows.filter(r => r.offer).map(r => r.offer.id)
      }
    },
    maybePoll() {
      const waiting = this.rows.some(row => this.rowState(row) === 'waiting')
      if (waiting) {
        this.startPolling()
      } else {
        this.stopPolling()
      }
    },
    startPolling() {
      if (this.pollTimer) {
        return
      }
      this.pollTimer = setInterval(async () => {
        await this.loadOffers()
        this.maybePoll()
      }, POLL_INTERVAL)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    }
  },
  async mounted() {
    await user.getUserBase()
    const companyInfo = await user.getCompanyInfo()
    this.companyTimezone = (companyInfo && companyInfo.timezone) || Intl.DateTimeFormat().resolvedOptions().timeZone
    await this.loadStores()
    if (this.selectedStore) {
      await this.loadData()
    }
  },
  beforeUnmount() {
    this.stopPolling()
  }
}
</script>

<style scoped>
.showcase-table th {
  font-size: 0.75rem;
  letter-spacing: 0.02em;
  color: #a1acb8;
  font-weight: 500;
  text-transform: uppercase;
}

.showcase-table th:first-child,
.showcase-table td:first-child {
  padding-left: 1.5rem;
}

.showcase-table th:last-child,
.showcase-table td:last-child {
  padding-right: 1.5rem;
}

.showcase-table .form-check-input {
  width: 1.15rem;
  height: 1.15rem;
  margin: 0;
  flex: 0 0 auto;
}

.product-link {
  color: #566a7f;
  text-decoration: none;
  cursor: pointer;
}

.product-link:hover {
  color: #7367f0;
}

.product-name {
  display: inline-block;
  white-space: normal;
}

.showcase-disabled {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f7f7fa;
  border: 1px solid #eceef1;
  border-radius: 10px;
  padding: 12px 14px;
}

.disabled-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #fff;
  border: 1px solid #eceef1;
  color: #a1acb8;
  flex: 0 0 auto;
}

.showcase-disabled .btn {
  flex: 0 0 auto;
}

.showcase-waiting {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff3e0;
  border-radius: 10px;
  padding: 12px 14px;
  color: #cc8a2e;
}

.waiting-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #fff;
  color: #f0a24b;
  flex: 0 0 auto;
}

.waiting-icon .ti {
  animation: showcase-spin 1.6s linear infinite;
}

@keyframes showcase-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.showcase-waiting .fw-medium {
  color: #b9772a;
}

.showcase-waiting .small {
  color: #c99a5f;
}

.showcase-prices {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.showcase-price-line {
  display: flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
}

.price-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  background: #a1acb8;
  flex: 0 0 auto;
}

.price-dot.card {
  background: #5b8def;
}

.price-label {
  min-width: 120px;
  color: #6b7a8d;
}

.price-badge {
  background: #e7f8f0;
  color: #17a673;
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-left: auto;
}

.price-new {
  font-weight: 600;
  min-width: 84px;
  text-align: right;
  background: transparent;
  box-shadow: none;
  border-radius: 0;
}

.price-new.default {
  color: #384551;
}

.price-new.card {
  color: #5b8def;
}

.showcase-updated {
  color: #a5adba;
}
</style>
